
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trek } from '@/lib/trek-data';
import { ArrowRight, Mountain, Calendar, ArrowUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

interface TrekCardProps {
  trek: Trek;
  delay?: number;
  hideFeatureBadge?: boolean;
}

const TrekCard = ({ trek, delay = 0, hideFeatureBadge = false }: TrekCardProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-800/80 dark:text-emerald-100';
      case 'Moderate': return 'bg-sky-100 text-sky-800 dark:bg-sky-800/80 dark:text-sky-100';
      case 'Difficult': return 'bg-amber-100 text-amber-800 dark:bg-amber-800/80 dark:text-amber-100';
      case 'Challenging': return 'bg-rose-100 text-rose-800 dark:bg-rose-800/80 dark:text-rose-100';
      default: return 'bg-muted text-muted-foreground dark:bg-muted dark:text-muted-foreground';
    }
  };
  
  // Create JSON-LD structured data for this trek
  const trekJsonLd = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": trek.name,
    "description": trek.description || `${trek.name} trek in ${trek.location}`,
    "url": `https://gotrekking.in/trek/${trek.id}`,
    "geo": {
      "@type": "GeoCoordinates",
      "elevation": trek.elevation
    },
    "touristType": ["Adventure Travel", "Hiking", "Trekking"],
    "image": trek.images[0]
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: delay * 0.1 }}
      className="group relative overflow-hidden rounded-xl bg-card border border-border/30 hover:border-border/70 dark:hover:border-border/50 transition-all duration-300 hover:shadow-lg"
    >
      {/* Add structured data for search engines */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(trekJsonLd)}
        </script>
      </Helmet>
      
      <Link to={`/trek/${trek.id}`}>
        <div className="relative h-52 overflow-hidden rounded-t-xl">
          <div className={`absolute inset-0 bg-muted dark:bg-muted/30 ${imageLoaded ? 'hidden' : ''}`}></div>
          <img
            src={trek.images[0]}
            alt={`${trek.name} - Trek in ${trek.location}`}
            className={`h-full w-full object-cover transition-all duration-500 group-hover:scale-105 ${
              imageLoaded ? '' : 'lazy-image'
            }`}
            onLoad={() => setImageLoaded(true)}
            loading="lazy"
          />
          
          <div className="absolute top-4 left-4 z-10">
            <Badge variant="secondary" className={`font-medium ${getDifficultyColor(trek.difficulty)}`}>
              {trek.difficulty}
            </Badge>
          </div>
          
          {trek.isTopTrek && !hideFeatureBadge && (
            <div className="absolute top-4 right-4 z-10">
              <Badge variant="default" className="bg-primary text-primary-foreground font-medium">
                Featured
              </Badge>
            </div>
          )}
        </div>

        <div className="p-5">
          <h3 className="text-xl font-medium mb-2 group-hover:text-primary dark:group-hover:text-primary transition-colors">
            {trek.name}
          </h3>
          
          <div className="flex items-center text-sm text-muted-foreground mb-3">
            <Mountain className="h-4 w-4 mr-1 text-primary" />
            <span>{trek.location}</span>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">Elevation</span>
              <div className="flex items-center">
                <ArrowUp className="h-3 w-3 mr-1 text-accent" />
                <span className="font-medium">{trek.elevation} {trek.elevationUnit}</span>
              </div>
            </div>
            
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">Best Season</span>
              <div className="flex items-center">
                <Calendar className="h-3 w-3 mr-1 text-primary" />
                <span className="font-medium text-sm">{trek.bestSeason}</span>
              </div>
            </div>
          </div>
          
          <div className="flex justify-between items-center pt-2 border-t border-border/30">
            <span className="text-sm font-medium text-card-foreground">Compare packages</span>
            <ArrowRight className="h-4 w-4 text-primary transition-transform group-hover:translate-x-1" />
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default TrekCard;
